import { useEffect, useState } from "react";
import { settings } from "app";
import permissions from "app/permissions";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { administrationRequests } from "api/administrationApi";
import {
  Button,
  Datagrid,
  FunctionField,
  List,
  NumberField,
  ReferenceField,
  SelectField,
  SelectInput,
  SimpleList,
  TextField,
  TextInput,
  Title,
  useNotify,
  useUnselectAll,
  useStore,
} from "react-admin";
import { Box, Typography, Paper, useMediaQuery } from "@mui/material";

import { AttestationStatusChoices, NationalityType } from "utils/choices";
import ManageattestationsDialog from "./ManageAttestationsDialog";
import UserField from "server/components/Fields/UserField";
import CustomFilter from "features/CustomFilter";
import {
  ATTESTATIONS,
  AttestationStatus,
  DISCIPLINES,
  MOBILE_BREAKPOINT,
  RECORDS,
  STUDENT_EDUCATION,
  StudentServicePath,
} from "utils/constants";
import ActionButtons from "./ActionButtons";
import { studentRequests } from "api/studentApi";
import SecuredComponent from "server/security/SecuredComponent";
import { AttestationsExporter } from "./XlsxExporter";
import { ExportXlsButton } from "features/ExportXlsxButton";
import { tenants } from "utils/tenants";
import { getProfessorRecordText } from "server/components/Fields/_fieldsHelper";
import { useNavigate } from "react-router";
import { employeeRequests } from "api/employeeApi";
import { useNotifyError } from "utils/useNotifyError";
import dataProvider from "server/dataProvider";
import { MobileListRow } from "features/MobileListRow";

const ListAttestations = () => {
  const notifyError = useNotifyError();
  const notify = useNotify();
  const navigate = useNavigate();
  const unselectAll = useUnselectAll(`${StudentServicePath}${ATTESTATIONS}`);
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  // const [isManageAttestations, setIsManageAttestations] = useState(false);
  // const [attestationsRecord, setAttestationsRecord] = useState(null);
  // const canManageAttestations = checkPermission(getPermissions(), permissions.canManageAttestations);

  const [isIsSelectedIds, setIsSelectedIds] = useState(false);

  const STORE_KEY = "attestations.filters";

  // filters drives the <List> and the gate (persisted via useStore);
  // the fields below are the draft selections bound to CustomFilter, committed on "Провери".
  const [filters, setFilters] = useStore(STORE_KEY, null);
  const [specialityId, setSpecialityId] = useState(filters?.specialityId ?? null);
  const [disciplineId, setDisciplineId] = useState(filters?.disciplineId ?? null);
  const [course, setCourse] = useState(filters?.course ?? null);
  const [studyGroup, setStudyGroup] = useState(filters?.studyGroup ?? null);
  const [instructionLanguageId, setInstructionLanguageId] = useState(filters?.instructionLanguageId ?? null);
  const [nationalityType, setNationalityType] = useState(filters?.nationalityType ?? null);

  const [specialiyChoices, setSpecialityChoices] = useState([]);
  const [disciplineChoices, setDisciplineChoices] = useState([]);
  const [coursesChoices, setCoursesChoices] = useState(null);
  const [studyGroupChoices, setStudyGroupChoices] = useState(null);
  const [instructionLanguageChoices, setInstructionLanguageChoices] = useState([]);

  const showInstructionLanguagesFilter = settings.tenant === tenants.MU;

  // Load specialities on mount; drop stale committed filters whose speciality no longer exists.
  useEffect(() => {
    dataProvider
      .customRequest(studentRequests.getAttestationSpecialitiesSelect())
      .then(res => {
        setSpecialityChoices(res.data);
        if (filters?.specialityId && !res.data.find(x => x.id === Number(filters.specialityId))) {
          setFilters(null);
          setSpecialityId(null);
          setDisciplineId(null);
          setCourse(null);
          setStudyGroup(null);
        }
      })
      .catch(error => {
        notifyError(error);
      });
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifyError]);

  // Instruction languages are an independent optional filter (hidden for MGU), loaded once on mount.
  useEffect(() => {
    if (!showInstructionLanguagesFilter) return;
    dataProvider
      .customRequest(administrationRequests.getInstructionLanguagesSelect())
      .then(res => {
        setInstructionLanguageChoices(res.data);
      })
      .catch(error => {
        notifyError(error);
      });
  }, [notifyError, showInstructionLanguagesFilter]);

  // Disciplines follow the draft speciality.
  useEffect(() => {
    if (!specialityId) {
      setDisciplineChoices([]);
      return;
    }
    dataProvider
      .customRequest(studentRequests.getAttestationDisciplinesSelect({ specialityId }))
      .then(res => {
        setDisciplineChoices(res.data);
      })
      .catch(error => {
        notifyError(error);
      });
  }, [specialityId, notifyError]);

  // Courses follow the draft speciality + discipline.
  useEffect(() => {
    if (!(disciplineId && specialityId)) {
      setCoursesChoices(null);
      return;
    }
    dataProvider
      .customRequest(studentRequests.getAttestationCoursesSelect({ disciplineId, specialityId }))
      .then(res => {
        setCoursesChoices(res.data);
      })
      .catch(error => {
        notifyError(error);
      });
  }, [disciplineId, specialityId, notifyError]);

  // Study groups follow the draft speciality + discipline + course.
  useEffect(() => {
    if (!(disciplineId && specialityId && course)) {
      setStudyGroupChoices(null);
      return;
    }
    dataProvider
      .customRequest(studentRequests.getAttestationStudyGroupsSelect({ disciplineId, specialityId, course }))
      .then(res => {
        setStudyGroupChoices(res.data);
      })
      .catch(error => {
        notifyError(error);
      });
  }, [course, disciplineId, specialityId, notifyError]);

  const searchParams = () => {
    if (specialityId && disciplineId && course) {
      unselectAll();
      setIsSelectedIds(false);
      setFilters({
        specialityId,
        disciplineId,
        course,
        ...(studyGroup && { studyGroup }),
        ...(instructionLanguageId && { instructionLanguageId }),
        ...(nationalityType && { nationalityType }),
      });
    } else {
      notify("Моля въведете специалност, дисциплина и курс!", { type: "error" });
    }
  };

  const listFilters = [
    <SelectInput
      source="status"
      label="Статус заверка"
      choices={AttestationStatusChoices}
      alwaysOn
      emptyText={"Всички"}
      style={{ width: 200 }}
      sx={isIsSelectedIds ? { marginBottom: "3.2rem" } : ""}
    />,
    <TextInput
      source="facultyNumber"
      label="Факултетен номер"
      alwaysOn
    />,
  ];

  return (
    <>
      {!filters && <Title title="Заверки" />}
      <CustomFilter
        title="Моля попълнете филтрите за да видите списъка със заверки"
        filters={[
          {
            label: "Специалност",
            value: specialityId,
            setValue: v => {
              setSpecialityId(v);
              setDisciplineId(null);
              setCourse(null);
              setStudyGroup(null);
            },
            type: "autocomplete",
            multiple: false,
            choices: specialiyChoices,
          },
          {
            label: "Дисциплина",
            value: disciplineId,
            setValue: v => {
              setDisciplineId(v);
              setCourse(null);
              setStudyGroup(null);
            },
            type: "autocomplete",
            multiple: false,
            choices: disciplineChoices,
            disable: !specialityId,
          },
          {
            label: "Курс",
            value: course,
            setValue: v => {
              setCourse(v);
              setStudyGroup(null);
            },
            type: "autocomplete",
            multiple: false,
            choices: coursesChoices,
            disable: !specialityId || !disciplineId,
          },
          {
            label: "Учебна група",
            value: studyGroup,
            setValue: setStudyGroup,
            type: "autocomplete",
            multiple: false,
            choices: studyGroupChoices,
            disable: !specialityId || !disciplineId || !course,
          },
          ...(showInstructionLanguagesFilter
            ? [
                {
                  label: "Език на обучение",
                  value: instructionLanguageId,
                  setValue: setInstructionLanguageId,
                  type: "autocomplete",
                  multiple: false,
                  choices: instructionLanguageChoices,
                  disable: !specialityId || !disciplineId || !course,
                },
                {
                  label: "Националност",
                  value: nationalityType,
                  setValue: setNationalityType,
                  type: "autocomplete",
                  multiple: false,
                  choices: NationalityType,
                  disable: !specialityId || !disciplineId || !course,
                },
              ]
            : []),
        ]}
        cardWidth={isMobile ? "350px" : "700px"}
        titleClasses={{
          minWidth: isMobile ? "250px" : "550px",
          maxWidth: "min-content",
          marginLeft: "auto",
          marginRight: "auto",
        }}
        buttonLabel="Провери"
        buttonOnClick={searchParams}
      />
      {!filters && (
        <Paper>
          <Box mt={3}>
            <Typography
              variant="inherit"
              align="center"
              color={"secondary"}
            >
              Въведи данни за търсене
            </Typography>
          </Box>
        </Paper>
      )}

      {filters && (
        <List
          actions={<ExportXlsButton exporter={AttestationsExporter} />}
          title="Заверки"
          sx={settings.style.expandStyle}
          filters={listFilters}
          filter={filters}
        >
          {isMobile ? (
            <SimpleList
              linkType={false}
              sx={settings.style.mobileList}
              primaryText={record => (
                <div>
                  <MobileListRow fields={[{ label: "Факултетен номер", value: record?.facultyNumber }]} />
                  <MobileListRow fields={[{ label: "Студент", value: <UserField source="studentUserId" /> }]} />
                </div>
              )}
              secondaryText={record => (
                <div>
                  <MobileListRow
                    fields={[
                      {
                        label: "Дисциплина",
                        value: (
                          <ReferenceField
                            source="disciplineId"
                            reference={administrationRequests.getDisciplinesSelect()}
                          >
                            <TextField source="name" />
                          </ReferenceField>
                        ),
                      },
                    ]}
                  />
                  <MobileListRow
                    fields={[
                      {
                        label: "Преподавател",
                        value: (
                          <ReferenceField
                            source="professorEmployeeId"
                            reference={employeeRequests.getProfessorsSelect()}
                          >
                            <FunctionField render={getProfessorRecordText} />
                          </ReferenceField>
                        ),
                      },
                    ]}
                  />
                  <MobileListRow
                    fields={[
                      {
                        label: "Тип занятие",
                        value: (
                          <ReferenceField
                            source="classTypeId"
                            reference={administrationRequests.getRoomClassTypesSelect()}
                          >
                            <TextField source="name" />
                          </ReferenceField>
                        ),
                      },
                    ]}
                  />
                  <MobileListRow
                    fields={[
                      {
                        label: "Статус заверка",
                        value: (
                          <SelectField
                            sx={{ color: record.status === AttestationStatus.Unverified ? "red" : "black" }}
                            source="status"
                            choices={AttestationStatusChoices}
                          />
                        ),
                      },
                    ]}
                  />
                  <MobileListRow
                    fields={[{ label: "Има малки деца", value: record?.youngChildrenMotherText || "-" }]}
                  />
                  {settings.tenant === tenants.MU && (
                    <MobileListRow fields={[{ label: "Коментар", value: record?.note || "-" }]} />
                  )}
                  <MobileListRow fields={[{ label: "Присъствия", value: record?.classesAttended }]} />
                  <MobileListRow fields={[{ label: "Отсъствия", value: record?.classesAbsent }]} />
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    <SecuredComponent permission={permissions.canManageAttestations}>
                      <ManageattestationsDialog attestationRecord={record} />
                    </SecuredComponent>
                    <SecuredComponent permission={permissions.canPreviewStudentRecords}>
                      <Button
                        label="Частичен картон"
                        variant="contained"
                        size="small"
                        startIcon={<ReceiptLongIcon />}
                        onClick={() =>
                          navigate(
                            `/${StudentServicePath}${RECORDS}/${STUDENT_EDUCATION}/${record?.studentEducationId}/${DISCIPLINES}/${record.disciplineId}`,
                            {
                              state: {
                                educationId: record?.studentEducationId,
                              },
                            },
                          )
                        }
                      />
                    </SecuredComponent>
                  </div>
                </div>
              )}
            />
          ) : (
            <Datagrid
              bulkActionButtons={<ActionButtons setIsSelectedIds={setIsSelectedIds} />}
              rowClick={false}
              sx={settings.style.firstExpandIconDatagrid}
            >
              <TextField
                source="facultyNumber"
                label="Факултетен номер"
              />
              <UserField
                label="Студент"
                source="studentUserId"
                sortable={false}
              />

              <ReferenceField
                label="Дисциплина"
                source="disciplineId"
                reference={administrationRequests.getDisciplinesSelect()}
                sortable={false}
              >
                <TextField
                  source="name"
                  label="Дисциплина"
                />
              </ReferenceField>

              <ReferenceField
                label="Преподавател"
                source="professorEmployeeId"
                reference={employeeRequests.getProfessorsSelect()}
                sortable={false}
              >
                <FunctionField render={getProfessorRecordText} />
              </ReferenceField>
              <ReferenceField
                source="classTypeId"
                reference={administrationRequests.getRoomClassTypesSelect()}
                label="Тип занятие"
                sortable={false}
              >
                <TextField source="name" />
              </ReferenceField>
              <FunctionField
                label="Статус заверка"
                render={record => (
                  <SelectField
                    sx={{ color: record.status === AttestationStatus.Unverified ? "red" : "black" }}
                    source="status"
                    choices={AttestationStatusChoices}
                  />
                )}
              />
              <TextField
                source="youngChildrenMotherText"
                label="Има малки деца"
                emptyText="-"
              />
              {settings.tenant === tenants.MU && (
                <TextField
                  label="Коментар"
                  source="note"
                  sortable={false}
                  emptyText="-"
                />
              )}
              <NumberField
                label="Присъствия"
                source="classesAttended"
                sortable={false}
              />

              <NumberField
                label="Отсъствия"
                source="classesAbsent"
                sortable={false}
              />
              <SecuredComponent permission={permissions.canManageAttestations}>
                <FunctionField
                  label="Действие"
                  render={record => <ManageattestationsDialog attestationRecord={record} />}
                />
              </SecuredComponent>
              <SecuredComponent permission={permissions.canPreviewStudentRecords}>
                <FunctionField
                  render={record => (
                    <Button
                      label="Частичен картон"
                      variant="contained"
                      size="small"
                      startIcon={<ReceiptLongIcon />}
                      onClick={() =>
                        navigate(
                          `/${StudentServicePath}${RECORDS}/${STUDENT_EDUCATION}/${record?.studentEducationId}/${DISCIPLINES}/${record.disciplineId}`,
                          {
                            state: {
                              educationId: record?.studentEducationId,
                            },
                          },
                        )
                      }
                    />
                  )}
                />
              </SecuredComponent>
            </Datagrid>
          )}
        </List>
      )}
    </>
  );
};

export default ListAttestations;
